import { set } from "lodash";
import React, { createContext, useContext, useEffect, useState } from "react";

interface UploadTask {
  id: string;
  file: File;
  signedUrl: string;
  progress: number;
  status: "uploading" | "success" | "error";
  abortController: AbortController;
  isUploading?: any;
}
interface UploadContextType {
  uploadTasks: UploadTask[];
  addUpload: (task: UploadTask) => void;
  updateUploadProgress: (id: string, progress: number) => void;
  finishUpload: (id: string, status: any) => void;
  removeUpload: (id: string | null) => void;
  addThumbnail: (task: { id: string; url: string; type?: string }) => void;
  removeThumbnail?: (() => void) | undefined;
  uploadTasksThumbnail: any;
}

const UploadContext = createContext<UploadContextType | undefined>(undefined);

export const UploadProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [uploadTasks, setUploadTasks] = useState<UploadTask[]>([]);
  const [uploadTasksThumbnail, setUploadTasksThumbnail]: any = useState([]);

  const removeThumbnail = () => {
    setUploadTasksThumbnail([]);
    setUploadTasks([]);
  };

  const addThumbnail = (task: { id: string; url: string; type?: string }) => {
    setUploadTasksThumbnail((prev: any) => [...prev, task]);
  };

  const addUpload = (task: UploadTask) => {
    setUploadTasks((prev) => [...prev, task]);
  };

  const updateUploadProgress = (id: string, progress: number) => {
    setUploadTasks((prev) =>
      prev.map((task) => (task.id === id ? { ...task, progress } : task))
    );
  };

  const finishUpload = (
    id: string,
    status: "uploading" | "success" | "error"
  ) => {
    setUploadTasks((prev) =>
      prev.map((task) =>
        task.id === id ? { ...task, isUploading: false, status } : task
      )
    );
  };

  const removeUpload = (id: string | null) => {
    if (id === null) {
      setUploadTasks([]);
      setUploadTasksThumbnail([]);
    } else {
      setUploadTasks((prev) => prev.filter((task) => task.id !== id));
      setUploadTasksThumbnail((prev: any) =>
        prev.filter((task: any) => task.id !== id)
      );
    }
  };

  // Handle refresh warning
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (uploadTasks && uploadTasks.length > 0) {
        e.preventDefault();
        e.returnValue = ""; // 👈 suppress TS warning, still works
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [uploadTasks]);

  return (
    <UploadContext.Provider
      value={{
        uploadTasks,
        addUpload,
        updateUploadProgress,
        finishUpload,
        removeUpload,
        addThumbnail,
        uploadTasksThumbnail,
        removeThumbnail,
      }}
    >
      {children}
    </UploadContext.Provider>
  );
};

export const useUpload = () => {
  const context = useContext(UploadContext);
  if (!context) throw new Error("useUpload must be used within UploadProvider");
  return context;
};
